import { animate, state, style, transition, trigger } from '@angular/animations';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, Optional, PLATFORM_ID } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { FormControl } from '@angular/forms';
import { NavigationExtras, Router } from '@angular/router';
import { CarrentFilters } from '@app/models/carrent/CarrentFilters';
import { FerienFIlters } from '@app/models/filtermodels/FerienFIlters';
import { KreuzfahrtFilters } from '@app/models/filtermodels/KreuzfahrtFilters';
import { ParamFilters } from '@app/models/filtermodels/ParamFilters';
import { Partner } from '@app/models/partner/IPartner';
import { AuthGuardService } from '@app/services/auth-guard.service';
import { EncryptionService } from '@app/services/encyption.service';
import { SearchService } from '@app/services/search.service';
import moment from 'moment';
import { debounceTime, filter, forkJoin, share, startWith, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { MEDIA_TOKEN } from '../../../../../tockens';

@Component({
    selector: 'clever-global',
    templateUrl: './global.component.html',
    styleUrls: ['./global.component.scss'],
    animations: [
        trigger('zoom', [
            state('leftBottom', style({ transform: 'scale(0.3)', transformOrigin: 'left bottom', opacity: '0' })),
            state('default', style({ transform: 'scale(1)', opacity: '1' })),
            transition('* <=> default', animate('10ms cubic-bezier(0.25,1,.25,1)'))
        ])
    ],
    standalone: false
})
export class GlobalFilterComponent implements OnInit, OnDestroy {
    protected unsubscribe$ = new Subject();
    isBrowser: boolean = false;
    showModal: boolean = false;
    focus: boolean = true;

    partner: Partner;
    ops: string = '';
    media: string = '';
    zielText: string = 'Reiseziel, Angebote';

    mySearchControl = new FormControl();

    origin: string = 'default';
    searchObj: any;
    searchres: any[] = [];
    loading: boolean = false;
    showglobalsearchres: boolean = false;

    constructor(
        protected mediaObserver: BreakpointObserver,
        protected cdr: ChangeDetectorRef,
        protected searchService: SearchService,
        protected guardService: AuthGuardService,
        protected encservice: EncryptionService,
        protected router: Router,
        @Inject(DOCUMENT) protected document: any,
        @Inject(PLATFORM_ID) protected platformId: any,
        @Optional() @Inject(MEDIA_TOKEN) private media_token: string
    ) {
        this.isBrowser = isPlatformBrowser(this.platformId);
        this.media = this.media_token;
    }

    ngOnInit(): void {
        this.initPartner();
        this.initMedia();
    }

    async initplaceholder() {
        this.zielText = 'Reiseziel, Angebote';
        if (!this.partner.nosktv) {
            this.zielText += ', Bestellnummer';
        }
    }

    initPartner(): void {
        this.guardService.getPartner$.pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(partner => {
            if (partner?.partnerid?.length > 0) {
                this.partner = partner;
                for (let i = 0; i < this.partner?.agenturen?.length; i++) {
                    if (this.partner?.agenturen[i]?.aktiv) {
                        this.ops += this.partner?.agenturen[i]?.code + ',';
                    }
                }

                this.initplaceholder();
                this.initSearchChange();
            }
        });
    }

    async initMedia() {
       if (this.mediaObserver.isMatched(Breakpoints.XSmall)) {
            this.media = 'xs';
        }
        if (this.mediaObserver.isMatched(Breakpoints.Small)) {
            this.media = 'sm';
        }
        if (this.mediaObserver.isMatched(Breakpoints.Medium)) {
            this.media = 'md';
        }
        if (this.mediaObserver.isMatched(Breakpoints.Large)) {
            this.media = 'lg';
        }
        if (this.mediaObserver.isMatched(Breakpoints.XLarge)) {
            this.media = 'xl';
        }
        this.cdr.detectChanges();
    }
    ngOnDestroy(): void {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }

    openModal(ev): void {
        ev?.stopPropagation();
        this.showModal = true;
        this.zoomin('leftBottom');
    }

    openModalMobile(ev): void {
        // this.render.addClass(document.body, 'model-open');
        ev?.stopPropagation();
        this.showModal = true;
        this.zoomin('leftBottom');
    }

    closeModal(): void {
        this.showModal = false;
        this.cdr.detectChanges();
    }

    closeModalMobile(): void {
        this.showModal = false;
        // this.render.removeClass(document.body, 'model-open');
        this.cdr.detectChanges();
    }

    zoomin(position: any): void {
        this.origin = position;
        this.cdr.detectChanges();
        setTimeout(() => {
            this.origin = 'default';
            this.cdr.detectChanges();
        });
    }

    detectPast(ev) {
        ev.stopPropagation();
        ev.preventDefault();
        this.mySearchControl.setValue(' ', { emitEvent: true, emitViewToModelChange: true });
        this.cdr.detectChanges();
        this.mySearchControl.setValue(ev.clipboardData.getData('text'), { emitEvent: true, emitViewToModelChange: true });
        this.cdr.detectChanges();
    }

    clearText(): void {
        this.mySearchControl.setValue('');
        this.focus = false;
        this.cdr.detectChanges();
        setTimeout(() => {
            this.focus = true;
            this.cdr.detectChanges();
        }, 50);
    }

    initSearchChange(): void {
        const filterShare = this.mySearchControl.valueChanges.pipe(
            startWith(''),
            debounceTime(100),
            tap(value => {
                this.loading = value?.length > 0;
                this.showglobalsearchres = false;
                this.cdr.detectChanges();
            }),
            filter(value => (typeof value === 'string' && value?.length >= 3)),
            share()
        );

        filterShare.pipe(
            takeUntil(this.unsubscribe$),
            debounceTime(300),
            switchMap(value => forkJoin(this.getSearchTarget(value)))
        ).subscribe(res => {
            // console.log(res);
            this.searchObj = res;
            this.sortSearchResult();
        });
    }

    getSearchTarget(value): any {
        const searchTarget = {
            pauschal: this.searchService.searchEntries({
                searchValue: value,
                productType: 'pauschal',
                subTypeRegion: true,
                subTypeLocation: true,
                subTypeGiataHotel: true,
                subTypeCountry: true,
                tourOperatorList: this.ops,
                limit: 10
            }, 'completions'),
            hotelonly: this.searchService.searchEntries({
                searchValue: value,
                productType: 'hotelonly',
                subTypeRegion: true,
                subTypeLocation: true,
                subTypeGiataHotel: true,
                subTypeCountry: true,
                tourOperatorList: this.ops,
                limit: 10
            }, 'completions'),
            flight: this.searchService.searchEntries({
                searchValue: value,
                productType: 'flight',
                subTypeDestinationAirport: true,
                tourOperatorList: this.ops,
                limit: 10
            }, 'completions'),
            railtravel: this.searchService.searchEntries({
                searchValue: value,
                productType: 'railtravel',
                subTypeRegion: true,
                subTypeLocation: true,
                subTypeGiataHotel: true,
                subTypeCountry: true,
                tourOperatorList: this.ops,
                limit: 10
            }, 'completions'),
            ferienhauser: this.searchService.searchEntries({
                apikey: this.partner?.hometogo?.apikey,
                origin: this.partner?.hometogo?.origin,
                searchtarget: value,
                suffix: 'completionland',
                limit: 10
            }, 'completions/completionFerienhauser'),
            ferienhauserRegion: this.searchService.searchEntries({
                searchtarget: value,
                suffix: 'completionRegions',
                limit: 10
            }, 'completions/completionFerienhauser'),
            ferienhauserOrts: this.searchService.searchEntries({
                searchtarget: value,
                suffix: 'completionOrts',
                limit: 10
            }, 'completions/completionFerienhauser'),
            tvangebote: this.searchService.searchEntries({
                searchtarget: value,
                limit: 10
            }, 'completions/completionTVAngebote'),
            cruise: this.searchService.searchEntries({
                searchtarget: value,
                ref: this.partner?.xt?.ref,
                limit: 10
            }, 'completions/completionCruise'),
            contents: this.searchService.searchEntries({
                searchtarget: value,
                partnerid: this.partner.partnerid,
                active: true,
                limit: 10
            }, 'completions/completionContent'),
        }

        if (this.partner.nosktv) {
            delete searchTarget.tvangebote;
        }

        if (isNaN(Number(this.partner.xt?.ref)) || Number(this.partner.xt?.ref) === 0) {
            delete searchTarget.cruise;
        }

        if (this.partner.hideferiensearch || !this.partner?.hometogo?.active) {
            delete searchTarget.ferienhauser;
        }

        return searchTarget;
    }

    async sortSearchResult() {
        const reg = new RegExp('(' + this.mySearchControl.value + ')', 'gim');
        this.searchres = [];
        for (let i = 0; i < this.searchObj?.pauschal?.length; i++) {
            const index = this.searchres.findIndex(obj => (obj.name === this.searchObj?.pauschal[i].name && obj?.subtext === this.searchObj?.pauschal[i]?.region?.name));

            if (index >= 0) {
                this.searchres[index].isPauschal = true;
                this.searchres[index].pauschal = this.searchObj?.pauschal[i];
            } else {
                this.searchres.push({
                    name: this.searchObj?.pauschal[i].name,
                    displayname: String(this.searchObj?.pauschal[i].name).replace(reg, '<mark>$1</mark>'),
                    pauschal: this.searchObj?.pauschal[i],
                    isPauschal: true,
                    subtext: this.searchObj?.pauschal[i]?.region?.name || this.searchObj?.pauschal[i]?.superRegion?.name || '',
                    score: this.searchObj?.pauschal[i].score
                });
            }
        }

        // nur hotel
        for (let i = 0; i < this.searchObj?.hotelonly?.length; i++) {
            const index = this.searchres.findIndex(obj => (obj.name === this.searchObj?.hotelonly[i].name && obj?.subtext === this.searchObj?.hotelonly[i]?.region?.name));

            if (index >= 0) {
                this.searchres[index].isHotelonly = true;
                this.searchres[index].hotelonly = this.searchObj?.hotelonly[i];
            } else {
                this.searchres.push({
                    name: this.searchObj?.hotelonly[i].name,
                    displayname: String(this.searchObj?.hotelonly[i].name).replace(reg, '<mark>$1</mark>'),
                    hotelonly: this.searchObj?.hotelonly[i],
                    isHotelonly: true,
                    subtext: this.searchObj?.hotelonly[i]?.region?.name || this.searchObj?.hotelonly[i]?.superRegion?.name || '',
                    score: this.searchObj?.hotelonly[i].score
                });
            }
        }

        // railtravel
        for (let i = 0; i < this.searchObj?.railtravel?.length; i++) {
            const index = this.searchres.findIndex(obj => obj.name === this.searchObj?.railtravel[i].name);

            if (index >= 0) {
                this.searchres[index].isRailtravel = true;
                this.searchres[index].railtravel = this.searchObj?.railtravel[i];
            } else {
                this.searchres.push({
                    name: this.searchObj?.railtravel[i].name,
                    displayname: String(this.searchObj?.railtravel[i].name).replace(reg, '<mark>$1</mark>'),
                    railtravel: this.searchObj?.pauschal[i]?.region?.name || this.searchObj?.pauschal[i]?.superRegion?.name || '',
                    isRailtravel: true,
                    subtext: this.searchObj?.railtravel[i]?.code,
                    score: this.searchObj?.railtravel[i].score
                });
            }
        }

        // flight
        for (let i = 0; i < this.searchObj?.flight?.length; i++) {
            const index = this.searchres.findIndex(obj => obj.name === this.searchObj?.flight[i].name);

            if (index >= 0) {
                this.searchres[index].isFlight = true;
                this.searchres[index].isCarrent = true;
                this.searchres[index].flight = this.searchObj?.flight[i];
            } else {
                this.searchres.push({
                    name: this.searchObj?.flight[i].name,
                    displayname: String(this.searchObj?.flight[i].name).replace(reg, '<mark>$1</mark>'),
                    flight: this.searchObj?.flight[i],
                    isFlight: true,
                    isCarrent: true,
                    subtext: this.searchObj?.flight[i]?.code,
                    score: this.searchObj?.flight[i].score
                });
            }
        }

        // ferienhauser
        for (let i = 0; i < this.searchObj?.ferienhauser?.length; i++) {
            const index = this.searchres.findIndex(obj => obj.name === this.searchObj?.ferienhauser[i].title);

            if (index >= 0) {
                this.searchres[index].isFerienhauser = true;
                this.searchres[index].ferienhauser = this.searchObj?.ferienhauser[i];
            } else {
                this.searchres.push({
                    name: this.searchObj?.ferienhauser[i].title,
                    id: this.searchObj?.ferienhauser[i].id,
                    displayname: String(this.searchObj?.ferienhauser[i].title).replace(reg, '<mark>$1</mark>'),
                    ferienhauser: this.searchObj?.ferienhauser[i],
                    isFerienhauser: true,
                    subtext: '',
                    score: this.searchObj?.ferienhauser[i].score
                });
            }
        }

        // cruise
        for (let i = 0; i < this.searchObj?.cruise?.length; i++) {
            const index = this.searchres.findIndex(obj => obj.name === this.searchObj?.cruise[i].name);

            if (index >= 0) {
                this.searchres[index].isCruise = true;
                this.searchres[index].cruise = this.searchObj?.cruise[i];
            } else {
                this.searchres.push({
                    name: this.searchObj?.cruise[i].name,
                    displayname: String(this.searchObj?.cruise[i].name).replace(reg, '<mark>$1</mark>'),
                    cruise: this.searchObj?.cruise[i],
                    isCruise: true,
                    subtext: '',
                    score: this.searchObj?.cruise[i].score
                })
            }
        }

        // contents
        for (let i = 0; i < this.searchObj?.contents?.length; i++) {
            this.searchres.push({
                // name: this.searchObj?.contents[i].title,
                name: this.searchObj?.contents[i].pathname,
                displayname: String(this.searchObj?.contents[i].pathname).replace(reg, '<mark>$1</mark>'),
                description: this.searchObj?.contents[i].description,
                content: this.searchObj?.contents[i],
                isContent: true,
                subtext: this.searchObj?.contents[i].description?.length > 200 ? this.searchObj?.contents[i].description.slice(0, 200) + '...' : this.searchObj?.contents[i].description,
                score: this.searchObj?.contents[i].score
            })
        }

        // tvangebote
        for (let i = 0; i < this.searchObj?.tvangebote?.length; i++) {
            this.searchres.push({
                // name: this.searchObj?.contents[i].title,
                name: this.searchObj?.tvangebote[i].name,
                sktvbestellnummer: String('SkTV Bestellnummer: ' + this.searchObj?.tvangebote[i].id).replace(reg, '<mark>$1</mark>'),
                displayname: String(this.searchObj?.tvangebote[i].name).replace(reg, '<mark>$1</mark>'),
                id: this.searchObj?.tvangebote[i].id,
                location: this.searchObj?.tvangebote[i].location,
                tvangebote: this.searchObj?.tvangebote[i],
                isTvangebote: true,
                subtext: this.searchObj?.tvangebote[i].location,
                score: this.searchObj?.tvangebote[i].score
            })
        }

        this.searchres.sort((a, b) => b.score - a.score);
        // console.log(this.searchres);
        this.showglobalsearchres = true;
        this.loading = false;
        this.cdr.detectChanges();
    }

    gotoSearch(ev) {
        ev?.stopPropagation();
        if (this.mySearchControl.value?.length >= 3) {
            const navigationExtras: NavigationExtras = {
                queryParams: { params: this.encservice.encrypt({ searchtarget: this.mySearchControl.value }) }
            };
            const url = this.router.createUrlTree(['search'], navigationExtras).toString();
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('visibility', 'hidden');
            link.click();
        }
    }

    gotofirst(item, ev) {
        // console.log(item)
        if (item.isPauschal) {
            this.gotoPauschal(item, ev);
        } else if (item.isHotelonly) {
            this.gotoHotel(item, ev);
        } else if (item.isRailtravel) {
            this.gotoRail(item, ev);
        } else if (item.isFlight) {
            this.gotoFlight(item, ev);
        } else if (item.isCarrent) {
            this.gotoCarrent(item, ev);
        } else if (item.isCruise) {
            this.gotoCruise(item, ev);
        } else if (item.isFerienhauser) {
            this.gotoFerienhauser(item, ev);
        } else if (item.isContent) {
            this.gotoContent(item, ev);
        } else if (item.isTvangebote) {
            this.gotoSktv(item, ev);
        }
    }

    async gotoSktv(obj, ev) {
        ev?.stopPropagation();
        const navigationExtras: NavigationExtras = {
            queryParams: { params: this.encservice.encrypt({ extra: { orderId: obj.id } }) }
        };
        const url = this.router.createUrlTree(['sktv'], navigationExtras).toString();
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('visibility', 'hidden');
        link.click();
    }

    gotoContent(obj, ev) {
        ev?.stopPropagation();
        // console.log(obj);
        if (obj?.content?.path?.length > 0 && obj?.content?.pathname?.length > 0) {
            const url = 'https://' + (this.document.domain === 'localhost' ? 'localhost:4200' : this.document.domain) + obj?.content?.path + '/' + obj?.content?.pathname;
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('visibility', 'hidden');
            link.click();
        }
    }

    gotoCruise(obj, ev) {
        ev?.stopPropagation();
        // console.log(obj);

        const myKrzfilters = {} as KreuzfahrtFilters;
        const today = new Date();
        myKrzfilters.productType = 'kreuzfahrt';
        myKrzfilters.cruisesuchetype = '';
        myKrzfilters.cruiseduration = '';
        myKrzfilters.cruisemaxanzpass = '';
        myKrzfilters.cruiseboardlanguage = '';
        myKrzfilters.cruisemaxprice = '';
        myKrzfilters.cruisecabincat = '';
        myKrzfilters.reference = this.partner.xt.ref;
        myKrzfilters.cruisestarthafen = null;
        myKrzfilters.cruisezielhafen = null;
        myKrzfilters.airports = null;
        myKrzfilters.cruisemaxanzpass = '';
        if (obj.cruise?.isRegion) {
            myKrzfilters.cruiseregion = [obj.cruise];
        }
        if (obj.cruise?.isCompany) {
            myKrzfilters.cruisecompanys = [obj.cruise];
        }
        myKrzfilters.reisedauerResult = {
            value: '7',
            name: '1 Woche'
        };
        myKrzfilters.reisendeResult = {
            adults: 2,
            children: []
        };

        myKrzfilters.reisezeitraumResult = {
            start: new Date(today.setDate(today.getDate() + 14)).toISOString(),
            end: new Date(today.setDate(today.getDate() + 30)).toISOString()
        };

        const url = this.router.createUrlTree(['cruise'], { queryParams: { params: this.encservice.encrypt(myKrzfilters), replaceUrl: true } }).toString();
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('visibility', 'hidden');
        link.click();
    }

    gotoCarrent(obj, ev) {
        const objFilter = {} as CarrentFilters;
        ev?.stopPropagation();
        // console.log(obj);

        objFilter.productType = 'carrent';
        objFilter.searchDate = '1, 30';
        objFilter.startNum = 1;
        objFilter.endNum = 99;
        objFilter.adults = '2';
        objFilter.startTime = '12:00';
        objFilter.endTime = '12:00';
        objFilter.arrivalAirportList = obj.flight;
        const url = this.router.createUrlTree(['carrent'], { queryParams: { params: this.encservice.encrypt(objFilter), replaceUrl: true } }).toString();
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('visibility', 'hidden');
        link.click();
    }

    gotoFlight(obj, ev) {
        ev?.stopPropagation();
        // console.log(obj);
        const realparams = {} as ParamFilters;
        realparams.productType = 'flight';
        realparams.reisezeitraumResult = {
            start: moment().add(4, 'days').format('YYYY-MM-DD'),
            end: moment().add(180, 'days').format('YYYY-MM-DD'),
            minDuration: 1,
            maxDuration: 99
        };

        realparams.reisendeResult = {
            adults: 2,
            children: []
        };

        realparams.reisedauerResult = {
            name: 'Beliebige',
            minDuration: '1',
            maxDuration: '30'
        };
        realparams.arrivalAirportList = [obj.flight];

        const url = this.router.createUrlTree(['flugs'], { queryParams: { params: this.encservice.encrypt(realparams), replaceUrl: true } }).toString();
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('visibility', 'hidden');
        link.click();
    }

    gotoRail(obj, ev) {
        ev?.stopPropagation();
        // console.log(obj);
        const realparams = {} as ParamFilters;
        realparams.productType = 'railtravel';
        realparams.reisezeitraumResult = {
            start: moment().add(4, 'days').format('YYYY-MM-DD'),
            end: moment().add(180, 'days').format('YYYY-MM-DD'),
            minDuration: 1,
            maxDuration: 99
        };

        realparams.reisendeResult = {
            adults: 2,
            children: []
        };

        realparams.reisedauerResult = {
            name: 'Beliebige',
            minDuration: '1',
            maxDuration: '30'
        };
        realparams.israiltravel = true;
        realparams.pauschalreiseResult = {
            type: obj.railtravel.type,
            data: {
                name: obj.railtravel.name,
                code: obj.railtravel.code
            }
        }

        const url = this.router.createUrlTree(['hotellist'], { queryParams: { params: this.encservice.encrypt(realparams), replaceUrl: true } }).toString();
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('visibility', 'hidden');
        link.click();
    }

    gotoHotel(obj, ev) {
        ev?.stopPropagation();
        // console.log(obj);
        const realparams = {} as ParamFilters;
        realparams.productType = 'hotelonly';
        realparams.reisezeitraumResult = {
            start: moment().add(4, 'days').format('YYYY-MM-DD'),
            end: moment().add(180, 'days').format('YYYY-MM-DD'),
            minDuration: 1,
            maxDuration: 30
        };

        realparams.reisendeResult = {
            adults: 2,
            children: []
        };

        realparams.reisedauerResult = {
            name: 'Beliebige',
            minDuration: '1',
            maxDuration: '30'
        };
        realparams.pauschalreiseResult = {
            type: obj.hotelonly.type,
            data: {
                name: obj.hotelonly.name,
                code: obj.hotelonly.code
            }
        }

        const url = this.router.createUrlTree(['hotellist'], { queryParams: { params: this.encservice.encrypt(realparams), replaceUrl: true } }).toString();
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('visibility', 'hidden');
        link.click();
    }

    gotoPauschal(obj, ev) {
        ev?.stopPropagation();
        // console.log(obj);

        const realparams = {} as ParamFilters;
        realparams.productType = 'pauschal';
        realparams.reisezeitraumResult = {
            start: moment().add(4, 'days').format('YYYY-MM-DD'),
            end: moment().add(180, 'days').format('YYYY-MM-DD'),
            minDuration: 1,
            maxDuration: 99
        };

        realparams.reisendeResult = {
            adults: 2,
            children: []
        };

        realparams.reisedauerResult = {
            name: 'Beliebige',
            minDuration: '1',
            maxDuration: '30'
        };
        realparams.pauschalreiseResult = {
            type: obj.pauschal.type,
            data: {
                name: obj.pauschal.name,
                code: obj.pauschal.code
            }
        }

        const url = this.router.createUrlTree(['hotellist'], { queryParams: { params: this.encservice.encrypt(realparams), replaceUrl: true } }).toString();
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('visibility', 'hidden');
        link.click();
    }

    gotoFerienhauser(obj, ev) {
        ev?.stopPropagation();
        // console.log(obj);

        const myferienfilters = {} as FerienFIlters;
        myferienfilters.productType = 'ferienhauser';
        myferienfilters.reisezielResult = {
            id: obj.ferienhauser.id,
            title: obj.ferienhauser.title
        };
        myferienfilters.minPrice = 9;
        myferienfilters.maxPrice = 1000;

        myferienfilters.bedrooms = 1;
        myferienfilters.toCenter = null;
        myferienfilters.toWater = null;
        myferienfilters.toSki = null;
        myferienfilters.rating = null;

        myferienfilters.adults = 2;
        myferienfilters.children = 0;
        myferienfilters.childrenAges = [];

        myferienfilters.dauer = '7';
        myferienfilters.start = moment().add(4, 'days').format('YYYY-MM-DD');
        myferienfilters.end = moment().add(180, 'days').format('YYYY-MM-DD');
        const url = this.router.createUrlTree(['ferienhause'], { queryParams: { params: this.encservice.encrypt(myferienfilters), replaceUrl: true } }).toString();
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('visibility', 'hidden');
        link.click();
    }
}
